<template>
<div>
  <Header />
<v-container fluid>
    <v-card>
      <v-card-title>
        <h2>Externe Änderungswünsche</h2>
        <v-spacer></v-spacer>
        <v-chip class="ma-2" color="primary">
          {{ orders.length }} Unbearbeitete Aufträge
        </v-chip>
      </v-card-title>
      
      <v-card-text>
        <!-- Filter and Search -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Suche nach Bestellnummer oder Kunde"
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn color="primary" @click="refreshData" :loading="loading">
              <v-icon left>mdi-refresh</v-icon>
              Aktualisieren
            </v-btn>
          </v-col>
        </v-row>

        <!-- Orders Table -->
        <v-data-table
          :headers="headers"
          :items="filteredOrders"
          :loading="loading"
          class="elevation-1"
          :items-per-page="10"
          @click:row="selectOrder"
          :single-select="true"
        >
          <template v-slot:item.createDate="{ item }">
            {{ formatDate(item.createDate) }}
          </template>
          
          <template v-slot:item.customer="{ item }">
            <div>
              <strong>{{ item.customer.name }}</strong><br>
              <small>{{ item.customer.email }}</small>
            </div>
          </template>
          
          <template v-slot:item.externalInstructions.createdAt="{ item }">
            {{ formatDate(item.externalInstructions.createdAt) }}
          </template>
          
          <template v-slot:item.actions="{ item }">
            <v-btn small color="primary" @click.stop="viewInstructions(item)">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Instructions Dialog -->
    <v-dialog v-model="dialog" max-width="900px" persistent>
      <v-card v-if="selectedOrder">
        <v-card-title>
          <span class="headline">Änderungswünsche für Bestellung {{ selectedOrder.orderId }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <!-- Order Info -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <h4>Kundeninformationen</h4>
              <p>
                <strong>Name:</strong> {{ selectedOrder.customer.name }}<br>
                <strong>Email:</strong> {{ selectedOrder.customer.email }}<br>
                <strong>PLZ:</strong> {{ selectedOrder.customer.zip }}
              </p>
            </v-col>
            <v-col cols="12" md="6">
              <h4>Bestellinformationen</h4>
              <p>
                <strong>Bestellnummer:</strong> {{ selectedOrder.orderId }}<br>
                <strong>Shopify ID:</strong> {{ selectedOrder.shopifyOrderId || 'N/A' }}<br>
                <strong>Erstellt am:</strong> {{ formatDate(selectedOrder.externalInstructions.createdAt) }}
              </p>
            </v-col>
          </v-row>

          <v-divider class="mb-4"></v-divider>

          <!-- Special Instructions Note -->
          <v-alert v-if="selectedOrder.externalInstructions.instructions[0] && selectedOrder.externalInstructions.instructions[0].instructions" 
                   type="info" 
                   outlined
                   class="mb-4">
            <strong>Kundenanweisung:</strong> {{ selectedOrder.externalInstructions.instructions[0].instructions }}
          </v-alert>

          <!-- Product Comparison -->
          <h4 class="mb-2">Produktänderungen</h4>
          
          <div v-if="editMode">
            <!-- Edit Mode -->
            <v-form ref="form" v-model="valid">
              <div v-for="(product, index) in editProducts" :key="'product-' + product.sku + '-' + index" class="mb-4">
                <v-card outlined>
                  <v-card-title class="pa-3">
                    <v-row align="center">
                      <v-col cols="auto">
                        <v-icon size="60">mdi-package-variant</v-icon>
                      </v-col>
                      <v-col>
                        <div class="subtitle-1">{{ product.name || product.sku }}</div>
                        <div class="caption">SKU: {{ product.sku }}</div>
                      </v-col>
                    </v-row>
                  </v-card-title>
                  
                  <v-card-text>
                    <!-- Basic Info -->
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="product.sku"
                          label="SKU"
                          readonly
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="product.name"
                          label="Produktname"
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="2">
                        <v-text-field
                          v-model.number="product.qty"
                          label="Menge"
                          type="number"
                          :rules="[v => v > 0 || 'Menge muss größer als 0 sein']"
                          dense
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <!-- Attributes -->
                    <div class="mt-3">
                      <div class="subtitle-2 mb-2">Attribute</div>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <tbody>
                            <tr v-for="(value, key) in product.attributes" :key="key">
                              <td width="30%"><strong>{{ key }}</strong></td>
                              <td>
                                <v-text-field
                                  v-model="product.attributes[key]"
                                  dense
                                  hide-details
                                  single-line
                                ></v-text-field>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </div>

                    <!-- Translated Data -->
                    <div class="mt-3">
                      <div class="subtitle-2 mb-2">Translated Data</div>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <tbody>
                            <tr v-for="(value, key) in product.translated" :key="key">
                              <td width="30%"><strong>{{ key }}</strong></td>
                              <td>
                                <v-text-field
                                  v-model="product.translated[key]"
                                  dense
                                  hide-details
                                  single-line
                                ></v-text-field>
                              </td>
                              <td width="50">
                                <v-btn icon small @click="removeTranslatedAttribute(index, key)">
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                            <tr>
                              <td colspan="3">
                                <v-btn small text @click="addTranslatedAttribute(index)">
                                  <v-icon small left>mdi-plus</v-icon>
                                  Translated Attribut hinzufügen
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </div>

                    <!-- Notes -->
                    <div class="mt-3" v-if="getInstructionForProduct(product.sku)">
                      <v-alert type="info" dense text>
                        <strong>Kundenanweisung:</strong> {{ getInstructionForProduct(product.sku).instructions }}
                      </v-alert>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
              
              <div class="mt-4">
                <v-alert type="info" dense text>
                  Bearbeiten Sie die Produkte gemäß den Kundenanweisungen. Die Änderungen werden direkt in den Produktdaten gespeichert.
                </v-alert>
              </div>
            </v-form>
          </div>
          <div v-else>
            <!-- View Mode - Product Comparison -->
            <div v-for="(instruction, index) in mergedProductData" :key="index" class="mb-4">
              <v-card>
                <v-card-title class="pa-3">
                  <v-row align="center">
                    <v-col cols="auto">
                      <v-img v-if="instruction.preview" :src="instruction.preview" width="80" height="80" contain></v-img>
                      <v-icon v-else size="80">mdi-image-off</v-icon>
                    </v-col>
                    <v-col>
                      <div class="headline">{{ instruction.title || instruction.name }}</div>
                      <div class="subtitle-1">SKU: {{ instruction.sku }}</div>
                      <v-chip v-if="instruction.hasChanges" color="warning" small>
                        <v-icon small left>mdi-alert</v-icon>
                        Änderungen vorhanden
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card-title>
                
                <v-card-text>
                  <v-row>
                    <!-- Original Product -->
                    <v-col cols="12" md="6">
                      <h5 class="mb-2">Original Produkt</h5>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <tbody>
                            <tr>
                              <td><strong>Menge</strong></td>
                              <td>{{ instruction.original.qty || 1 }}</td>
                            </tr>
                            <tr v-for="(value, key) in instruction.original.attributes" :key="'orig-attr-' + key">
                              <td><strong>{{ key }}</strong></td>
                              <td>{{ value }}</td>
                            </tr>
                            <tr v-for="(value, key) in instruction.original.translated" :key="'orig-trans-' + key">
                              <td><strong>{{ key }} (translated)</strong></td>
                              <td>{{ value }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-col>
                    
                    <!-- Changed Product -->
                    <v-col cols="12" md="6">
                      <h5 class="mb-2">
                        Gewünschte Änderungen
                        <v-icon v-if="instruction.hasChanges" color="warning" small>mdi-alert</v-icon>
                      </h5>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <tbody>
                            <tr :class="{ 'yellow lighten-4': instruction.changed.qty !== instruction.original.qty }">
                              <td><strong>Menge</strong></td>
                              <td>{{ instruction.changed.qty || 1 }}</td>
                            </tr>
                            <tr v-for="(value, key) in instruction.changed.attributes" 
                                :key="'chg-attr-' + key"
                                :class="{ 'yellow lighten-4': value !== instruction.original.attributes[key] }">
                              <td><strong>{{ key }}</strong></td>
                              <td>{{ value }}</td>
                            </tr>
                            <tr v-for="(value, key) in instruction.changed.translated" 
                                :key="'chg-trans-' + key"
                                :class="{ 'yellow lighten-4': value !== instruction.original.translated[key] }">
                              <td><strong>{{ key }} (translated)</strong></td>
                              <td>{{ value }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                      <v-alert v-if="instruction.changed.instructions" 
                               type="info" 
                               dense 
                               text
                               class="mt-2">
                        <strong>Anweisung:</strong> {{ instruction.changed.instructions }}
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-btn v-if="!editMode" color="warning" @click="toggleEditMode">
            <v-icon left>mdi-pencil</v-icon>
            Bearbeiten
          </v-btn>
          <v-btn v-else color="grey" @click="cancelEdit">
            Abbrechen
          </v-btn>
          <v-btn v-if="editMode" color="success" @click="saveInstructions" :loading="saving">
            <v-icon left>mdi-content-save</v-icon>
            Speichern
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="markAsProcessed" :loading="processing">
            <v-icon left>mdi-check</v-icon>
            Als bearbeitet markieren
          </v-btn>
          <v-btn color="error" @click="deleteInstructions" :loading="deleting">
            <v-icon left>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</div>
</template>

<script>
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import ExternalInstructionsService from '@/services/ExternalInstructionsService';
import Header from './header';

export default {
  name: 'ExternalInstructions',
  components: {
    Header
  },
  data() {
    return {
      orders: [],
      loading: false,
      search: '',
      dialog: false,
      selectedOrder: null,
      editMode: false,
      editProducts: [],
      editInstructions: [],
      valid: true,
      saving: false,
      processing: false,
      deleting: false,
      headers: [
        { text: 'Bestellnummer', value: 'orderId' },
        { text: 'Kunde', value: 'customer', sortable: false },
        { text: 'Produkte', value: 'products.length' },
        { text: 'Erstellt am', value: 'externalInstructions.createdAt' },
        { text: 'Status', value: 'state' },
        { text: 'Aktionen', value: 'actions', sortable: false }
      ]
    };
  },
  computed: {
    filteredOrders() {
      if (!this.search) return this.orders;
      
      const searchTerm = this.search.toLowerCase();
      return this.orders.filter(order => {
        return order.orderId.toLowerCase().includes(searchTerm) ||
               order.customer.name.toLowerCase().includes(searchTerm) ||
               order.customer.email.toLowerCase().includes(searchTerm);
      });
    },
    
    mergedProductData() {
      if (!this.selectedOrder) return [];
      
      const originalProducts = this.selectedOrder.products || [];
      const instructions = this.selectedOrder.externalInstructions.instructions || [];
      
      // Create a map of original products by SKU
      const productMap = new Map();
      originalProducts.forEach(product => {
        productMap.set(product.sku, product);
      });
      
      // Merge with instructions
      return instructions.map(instruction => {
        const originalProduct = productMap.get(instruction.sku) || {};
        
        // Check if there are any changes
        const hasChanges = this.hasProductChanges(originalProduct, instruction);
        
        return {
          sku: instruction.sku,
          title: instruction.title,
          name: originalProduct.name,
          preview: instruction.preview,
          hasChanges,
          original: {
            qty: originalProduct.qty || 1,
            attributes: originalProduct.attributes || {},
            translated: originalProduct.translated || {}
          },
          changed: {
            qty: instruction.qty || 1,
            attributes: instruction.attributes || {},
            translated: instruction.translated || {},
            instructions: instruction.instructions
          }
        };
      });
    }
  },
  mounted() {
    this.fetchOrders();
  },
  methods: {
    async fetchOrders() {
      this.loading = true;
      try {
        const response = await ExternalInstructionsService.getUnprocessedInstructions();
        this.orders = response.data.orders || [];
      } catch (error) {
        console.error('Error fetching orders:', error);
        this.$toast.error('Fehler beim Laden der Bestellungen');
      } finally {
        this.loading = false;
      }
    },
    
    async refreshData() {
      await this.fetchOrders();
      this.$toast.success('Daten aktualisiert');
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: de });
    },
    
    selectOrder(order) {
      this.viewInstructions(order);
    },
    
    viewInstructions(order) {
      this.selectedOrder = order;
      this.editMode = false;
      this.dialog = true;
    },
    
    closeDialog() {
      this.dialog = false;
      this.selectedOrder = null;
      this.editMode = false;
      this.editInstructions = [];
    },
    
    toggleEditMode() {
      this.editMode = true;
      // Deep copy products for editing
      this.editProducts = JSON.parse(JSON.stringify(
        this.selectedOrder.products || []
      ));
      // Ensure each product has required objects
      this.editProducts.forEach(product => {
        if (!product.translated) {
          product.translated = {};
        }
        if (!product.attributes) {
          product.attributes = {};
        }
      });
    },
    
    cancelEdit() {
      this.editMode = false;
      this.editProducts = [];
    },
    
    addInstruction() {
      this.editInstructions.push({
        sku: '',
        title: '',
        qty: 1,
        attributes: {},
        translated: {},
        instructions: ''
      });
    },
    
    addTranslatedAttribute(productIndex) {
      const attributeName = prompt('Attribut Name:');
      if (attributeName) {
        if (!this.editProducts[productIndex].translated) {
          this.$set(this.editProducts[productIndex], 'translated', {});
        }
        this.$set(this.editProducts[productIndex].translated, attributeName, '');
      }
    },
    
    removeTranslatedAttribute(productIndex, key) {
      this.$delete(this.editProducts[productIndex].translated, key);
    },
    
    getInstructionForProduct(sku) {
      if (!this.selectedOrder || !this.selectedOrder.externalInstructions) return null;
      return this.selectedOrder.externalInstructions.instructions.find(i => i.sku === sku);
    },
    
    removeInstruction(index) {
      this.editInstructions.splice(index, 1);
    },
    
    hasProductChanges(original, changed) {
      // Check quantity
      if ((original.qty || 1) !== (changed.qty || 1)) return true;
      
      // Check attributes
      const origAttrs = original.attributes || {};
      const chgAttrs = changed.attributes || {};
      
      for (const key in origAttrs) {
        if (origAttrs[key] !== chgAttrs[key]) return true;
      }
      
      for (const key in chgAttrs) {
        if (!(key in origAttrs)) return true;
      }
      
      // Check translated
      const origTrans = original.translated || {};
      const chgTrans = changed.translated || {};
      
      for (const key in origTrans) {
        if (origTrans[key] !== chgTrans[key]) return true;
      }
      
      for (const key in chgTrans) {
        if (!(key in origTrans)) return true;
      }
      
      return false;
    },
    
    async saveInstructions() {
      if (!this.$refs.form.validate()) return;
      
      this.saving = true;
      try {
        await ExternalInstructionsService.updateInstructions(this.selectedOrder.orderId, {
          products: this.editProducts
        });
        
        // Update local data
        this.selectedOrder.products = this.editProducts;
        this.editMode = false;
        this.$toast.success('Änderungen gespeichert');
        
        // Refresh the merged data
        this.$forceUpdate();
      } catch (error) {
        console.error('Error saving instructions:', error);
        this.$toast.error('Fehler beim Speichern');
      } finally {
        this.saving = false;
      }
    },
    
    async markAsProcessed() {
      if (!confirm('Möchten Sie diese Änderungswünsche als bearbeitet markieren?')) return;
      
      this.processing = true;
      try {
        await ExternalInstructionsService.markAsProcessed(this.selectedOrder.orderId);
        
        // Remove from list
        const index = this.orders.findIndex(o => o.orderId === this.selectedOrder.orderId);
        if (index > -1) {
          this.orders.splice(index, 1);
        }
        
        this.closeDialog();
        this.$toast.success('Als bearbeitet markiert');
      } catch (error) {
        console.error('Error marking as processed:', error);
        this.$toast.error('Fehler beim Markieren');
      } finally {
        this.processing = false;
      }
    },
    
    async deleteInstructions() {
      if (!confirm('Möchten Sie diese Änderungswünsche wirklich löschen?')) return;
      
      this.deleting = true;
      try {
        await ExternalInstructionsService.deleteInstructions(this.selectedOrder.orderId);
        
        // Remove from list
        const index = this.orders.findIndex(o => o.orderId === this.selectedOrder.orderId);
        if (index > -1) {
          this.orders.splice(index, 1);
        }
        
        this.closeDialog();
        this.$toast.success('Änderungswünsche gelöscht');
      } catch (error) {
        console.error('Error deleting instructions:', error);
        this.$toast.error('Fehler beim Löschen');
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.v-data-table >>> tbody tr {
  cursor: pointer;
}

.v-data-table >>> tbody tr:hover {
  background-color: #f5f5f5;
}
</style>